import { ReactNode } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * Componente wrapper para listas animadas com efeito de dispersão/reunião
 * 
 * Funcionalidades:
 * - Anima entrada: elementos dispersam a partir do centro/base da tela
 * - Anima saída: elementos se reúnem no centro e desaparecem
 * - Usa stagger para criar efeito em cascata suave
 * - Mantém layout e estrutura HTML/CSS existentes
 * 
 * @param children - Elementos filhos que serão animados
 * @param key - Chave única para identificar quando a lista muda (ex: filtro, search)
 * @param className - Classe CSS adicional para o container
 */
interface AnimatedListProps {
  children: ReactNode;
  listKey: string | number; // Chave que muda quando a lista precisa reanimar
  className?: string;
}

/**
 * Variantes de animação para entrada (dispersão)
 * Elementos começam no centro (scale 0, opacity 0) e se espalham para suas posições
 */
const containerVariants = {
  hidden: {
    // Estado inicial: elementos invisíveis no centro
    opacity: 0,
  },
  visible: {
    // Estado visível: container pronto para animar filhos
    opacity: 1,
    transition: {
      // Delay inicial antes de começar a animação dos filhos
      delayChildren: 0.1,
      // Stagger: delay entre cada elemento filho (efeito cascata)
      staggerChildren: 0.05, // 50ms entre cada card
    },
  },
  exit: {
    // Estado de saída: elementos se reúnem no centro
    opacity: 0,
    transition: {
      // Stagger reverso: últimos elementos saem primeiro
      staggerChildren: 0.03,
      // Direção reversa para efeito de reunião
      staggerDirection: -1,
      // Delay antes de começar a saída
      delayChildren: 0,
    },
  },
};

// Nota: As variantes de animação para itens individuais estão definidas
// em AnimatedItem.tsx para evitar duplicação e manter a lógica separada

function AnimatedList({ children, listKey, className = '' }: AnimatedListProps) {
  return (
    <AnimatePresence mode="wait">
      {/* 
        AnimatePresence gerencia a animação de saída dos elementos
        mode="wait" garante que elementos antigos saiam antes dos novos entrarem
      */}
      <motion.div
        key={listKey} // Key muda quando a lista precisa reanimar (ex: filtro mudou)
        className={className}
        variants={containerVariants}
        initial="hidden" // Estado inicial
        animate="visible" // Estado animado
        exit="exit" // Estado de saída
      >
        {/* 
          Os filhos são renderizados normalmente, mas recebem as animações
          através do motion.div wrapper e do AnimatedItem
        */}
        {children}
      </motion.div>
    </AnimatePresence>
  );
}

export default AnimatedList;

